(() => {
  if (window.pwPreorderInstalled) return;
  window.pwPreorderInstalled = true;
  let checking = false;
  let bypass = false;
  const route = (path) => (window.Shopify?.routes?.root || '/') + path;
  const ownedKeys = ['Vooruitbestelling', 'Pre-order', 'Précommande', 'Verzendverwachting', 'Expected dispatch', 'Expédition prévue'];

  function isLegacyPreorderProperty(key, value) {
    if (!/^\d+$/.test(key)) return false;
    for (let depth = 0; depth < 64; depth += 1) {
      if (typeof value === 'string') {
        try { value = JSON.parse(value); }
        catch {
          try {
            value = JSON.parse(value.replace(/&quot;/g, '"').replace(/&#39;|&#x27;/g, "'").replace(/&amp;/g, '&'));
          } catch { return false; }
        }
      }
      if (!Array.isArray(value) || value.length !== 2) return false;
      const [name, content] = value;
      if (name === '_pw_preorder_key') return ownedKeys.includes(content);
      if (ownedKeys.includes(name)) {
        return typeof content === 'string' && /^(Verwachte verzending:|Expected dispatch:|Expédition prévue\s*:)/.test(content.replace(/^(?:\s|\\[rn])*/, ''));
      }
      if (name !== key) return false;
      value = content;
    }
    return false;
  }

  function nextProperties(line) {
    const previous = line.properties || {};
    const next = { ...previous };
    let removedLegacy = false;
    for (const [key, value] of Object.entries(previous)) {
      if (isLegacyPreorderProperty(key, value)) {
        delete next[key];
        removedLegacy = true;
      }
    }
    const oldKey = previous._pw_preorder_key;
    if (ownedKeys.includes(oldKey)) delete next[oldKey];
    delete next._pw_preorder_key;
    if (line.active) {
      next[line.label] = line.message;
      next._pw_preorder_key = line.label;
    } else if (oldKey || removedLegacy) {
      next._pw_preorder_key = '';
    }
    return next;
  }

  function equalProperties(a, b) {
    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
    return Array.from(keys).every((key) => (a?.[key] || '') === (b?.[key] || ''));
  }

  async function cartState() {
    const url = new URL(route('cart'), location.origin);
    url.searchParams.set('section_id', 'pw-preorder-cart-data');
    url.searchParams.set('_pw', String(Date.now()));
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { 'Accept': 'text/html' } });
    if (!response.ok) throw new Error('state');
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const node = document.querySelector('[data-pw-preorder-cart-data]');
    if (!node) throw new Error('missing-section');
    const state = JSON.parse(node.textContent);
    if (!Array.isArray(state.lines)) throw new Error('invalid-state');
    state.lines = state.lines.map((line) => {
      let properties = line.properties;
      if (Array.isArray(properties)) {
        if (!properties.every((entry) => Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string')) {
          throw new Error('invalid-properties');
        }
        properties = Object.fromEntries(properties);
      } else if (properties == null) {
        properties = {};
      } else if (typeof properties !== 'object') {
        throw new Error('invalid-properties');
      }
      return { ...line, properties };
    });
    return state;
  }

  async function synchronize(onStatus) {
    let state = await cartState();
    const maxUpdates = state.lines.length + 3;
    for (let attempt = 0; attempt < maxUpdates; attempt += 1) {
      onStatus(state);
      const line = state.lines.find((item) => !equalProperties(item.properties, nextProperties(item)));
      if (!line) return;
      const response = await fetch(route('cart/change.js'), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: nextProperties(line) })
      });
      if (!response.ok) throw new Error('change');
      state = await cartState();
    }
    throw new Error('cart-keeps-changing');
  }

  function syncForm(form) {
    const root = form.closest('[data-pw-preorder-configured="true"]');
    if (!root) return;
    const node = root.querySelector('[data-pc-fulfilment-variants]');
    if (!node) return;
    try {
      const variants = JSON.parse(node.textContent);
      const id = form.querySelector('[name="id"]')?.value;
      const variant = variants[id];
      if (!variant) return;
      const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
      const active = variant.inventoryTracked && variant.inventoryPolicy === 'continue' && Number(variant.inventoryQuantity) < quantity;
      form.querySelectorAll('[data-pw-preorder-property]').forEach((input) => { input.disabled = !active; });
    } catch (error) { console.warn('PW preorder: invalid variant data'); }
  }

  function checkoutTarget(event) {
    if (event.type === 'submit') {
      const form = event.target;
      const submitter = event.submitter;
      if (form instanceof HTMLFormElement && submitter?.matches('[name="checkout"], [data-cart-checkout-page-button]')) return submitter;
      return null;
    }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    const target = event.target.closest('a[href], button[name="checkout"], [data-cart-checkout-button], [data-cart-checkout-page-button]');
    if (!target || target.disabled || target.classList.contains('disabled') || target.hasAttribute('data-pw-request-checkout')) return null;
    if (target.matches('a[href]')) {
      const url = new URL(target.href, location.origin);
      if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) return null;
    }
    return target;
  }

  async function intercept(event) {
    if (event.type === 'submit' && event.target instanceof HTMLFormElement) syncForm(event.target);
    if (bypass || !document.querySelector('[data-pw-preorder-cart-watch]')) return;
    const target = checkoutTarget(event);
    if (!target) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checking) return;
    checking = true;
    const oldBusy = target.getAttribute('aria-busy');
    target.setAttribute('aria-busy', 'true');
    const status = document.createElement('span');
    status.className = 'pw-preorder-checkout-status';
    status.setAttribute('role', 'status');
    const watch = document.querySelector('[data-pw-preorder-cart-watch]');
    status.textContent = watch.dataset.checking;
    target.insertAdjacentElement('afterend', status);
    let errorMessage = watch.dataset.error;
    const locks = [];
    document.querySelectorAll('[data-js-cart-item] input, [data-js-cart-item] button, [data-cart-item] input, [data-cart-item] button').forEach((node) => {
      locks.push([node, node.disabled]);
      node.disabled = true;
    });
    try {
      await synchronize((state) => { status.textContent = state.checking; errorMessage = state.error; });
      status.remove();
      locks.forEach(([node, disabled]) => { node.disabled = disabled; });
      bypass = true;
      if (event.type === 'submit') event.target.requestSubmit(target);
      else target.click();
    } catch (error) {
      console.warn('PW preorder checkout:', error.message);
      status.setAttribute('role', 'alert');
      status.textContent = errorMessage;
      window.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      bypass = false;
      checking = false;
      locks.forEach(([node, disabled]) => { node.disabled = disabled; });
      if (oldBusy === null) target.removeAttribute('aria-busy');
      else target.setAttribute('aria-busy', oldBusy);
    }
  }

  document.addEventListener('click', intercept, true);
  document.addEventListener('submit', intercept, true);
})();